import {
  AuiIf,
  ComposerPrimitive,
  type AssistantState,
  useAuiState,
  useAui,
} from '@assistant-ui/react';
import Close from '@mui/icons-material/Close';
import Description from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingValues } from '@/features/settings';

/** Fallback when no org limit is configured — mirrors the backend default (MT1-1430). */
const DEFAULT_AI_MESSAGE_MAX_LENGTH = 400;

export const ComposerArea = () => {
  const { t } = useTranslation();
  const { getSettingValue } = useSettingValues();

  const messageMaxLength =
    getSettingValue<{ messageMaxLength?: number }>('ai-usage-limits', {}).messageMaxLength ??
    DEFAULT_AI_MESSAGE_MAX_LENGTH;
  const composerText = useAuiState((s) => s.composer.text);
  const charCount = composerText.length;
  const isOverLimit = charCount > messageMaxLength;

  return (
    // ComposerPrimitive.Root renders a <form> by default — Enter triggers submit
    <ComposerPrimitive.Root
      style={{
        borderTop: '1px solid',
        borderColor: 'rgba(0,0,0,0.12)',
        padding: 8,
        flexShrink: 0,
      }}
    >
      <ComposerPrimitive.AttachmentDropzone asChild>
        <Box sx={{ position: 'relative' }}>
          <ComposerPrimitive.Attachments>
            {() => <AttachmentPreviewItem />}
          </ComposerPrimitive.Attachments>

          <Box display="flex" alignItems="flex-end" gap={0.5}>
            <ComposerPrimitive.Input
              submitMode="enter"
              addAttachmentOnPaste
              placeholder={t('aiChat.input.placeholder')}
              // Match the disabled send button: block the Enter submit path when over the
              // limit. `isComposing` keeps IME (e.g. Japanese) confirmation Enters working.
              onKeyDown={(e) => {
                if (isOverLimit && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              style={{
                flex: 1,
                resize: 'none',
                border: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: '16px',
                lineHeight: 1.5,
                padding: '8px',
                background: 'transparent',
              }}
            />

            <AuiIf condition={(s: AssistantState) => !s.thread.isRunning}>
              <ComposerPrimitive.Send asChild>
                <Tooltip
                  title={
                    isOverLimit
                      ? t('aiChat.error.messageTooLong', { maxLength: messageMaxLength })
                      : t('aiChat.input.send')
                  }
                >
                  {/* span keeps the tooltip working while the button is disabled */}
                  <span>
                    <IconButton
                      color="primary"
                      aria-label={t('aiChat.input.send')}
                      disabled={isOverLimit}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </ComposerPrimitive.Send>
            </AuiIf>

            <AuiIf condition={(s: AssistantState) => s.thread.isRunning}>
              <ComposerPrimitive.Cancel asChild>
                <Tooltip title={t('aiChat.streaming.stop')}>
                  <IconButton
                    color="primary"
                    aria-label={t('aiChat.streaming.stop')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <StopIcon />
                  </IconButton>
                </Tooltip>
              </ComposerPrimitive.Cancel>
            </AuiIf>
          </Box>

          <Typography
            variant="caption"
            aria-live="polite"
            sx={{
              display: 'block',
              textAlign: 'right',
              px: 1,
              color: isOverLimit ? 'error.main' : 'text.secondary',
            }}
          >
            {charCount} / {messageMaxLength}
          </Typography>
        </Box>
      </ComposerPrimitive.AttachmentDropzone>
    </ComposerPrimitive.Root>
  );
};

const AttachmentPreviewItem = () => {
  const attachment = useAuiState((s) => s.attachment);
  const runtime = useAui();

  const isImage =
    (attachment.contentType?.startsWith('image/') ?? false) ||
    (attachment.type === 'file' &&
      ((attachment as { file?: File }).file?.type.startsWith('image/') ?? false));

  const name = attachment.name;

  const handleRemove = () => {
    runtime.attachment().remove().catch(console.error);
  };

  const file =
    isImage && attachment.type === 'file' ? (attachment as { file?: File }).file : undefined;
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      // eslint-disable-next-line n/no-unsupported-features/node-builtins
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (isImage && attachment.type === 'file') {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          display: 'inline-block',
          mr: 0.5,
          mb: 0.5,
        }}
      >
        {previewUrl && (
          <img
            src={previewUrl}
            alt={name}
            width={40}
            height={40}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <IconButton
          size="small"
          onClick={handleRemove}
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'grey.200' },
            padding: '2px',
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Chip
      icon={<Description fontSize="small" />}
      label={name}
      onDelete={handleRemove}
      size="small"
      deleteIcon={<Close fontSize="small" />}
      sx={{ maxWidth: 120, mr: 0.5, mb: 0.5 }}
    />
  );
};
